import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { VStack } from "../../../../styled-system/jsx";
import Card from "../../../components/core/basic/Card";
import { PageContainer } from "../../../components/core/basic/PageContainer";
import { Tabs, TabList, Tab, TabPanel } from "../../../components/core/basic/Tabs";
import BreadcrumbDates, { type Breadcrumb } from "../../../components/core/blocks/BreadcrumbDates";
import { PeriodPicker } from "../../../components/fields/base/Datepicker";
import { DatesProvider } from "../../../dates/DatesContext";
import useQueryParams from "../../../hooks/useQueryParams";
import { ConfigTab } from "../../../modules/goals/dashboard_tabs/ConfigTab";
import { DetailsTab } from "../../../modules/goals/dashboard_tabs/DetailsTab";
import { OverviewTab } from "../../../modules/goals/dashboard_tabs/OverviewTab";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../../../utils/dashboardLinks";

declare module "highcharts/highcharts.src" {
  interface Point {
    cumulativeSum: number;
  }
}

function GoalsDashboard() {
  const { t } = useTranslation(["goals"]);
  const [displayPeriod, setDisplayPeriod] = useState(new Date());

  const [tab, setTab] = useQueryParams("tab", "details");

  const breadCrumbItems: Breadcrumb[] = [
    AGENCY_DASHBOARD_BREADCRUMB_SPEC,
    {
      name: t(($) => $.goals["Goals Dashboard"]),
      href: "/user/goals",
      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      dropdownSubtitle: t(($) => $.goals["Reports"]),
    },
  ];

  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} hideDateSelector>
        <PeriodPicker
          period={displayPeriod}
          setPeriod={setDisplayPeriod}
          periodType={tab === "details" ? "month" : "year"}
        />
      </BreadcrumbDates>
      <PageContainer withBreadcrumb>
        <VStack alignItems="stretch">
          <Card>
            <Tabs selectedKey={tab ?? undefined} onSelectionChange={(key) => setTab(key as string)}>
              <TabList>
                <Tab id="details">{t(($) => $.goals["Details"])}</Tab>
                <Tab id="overview">{t(($) => $.goals["Overview"])}</Tab>
                <Tab id="config">{t(($) => $.goals["Configuration"])}</Tab>
              </TabList>
              <TabPanel id="details">
                <DetailsTab displayPeriod={displayPeriod} />
              </TabPanel>
              <TabPanel id="overview">
                <OverviewTab displayPeriod={displayPeriod} setDisplayPeriod={setDisplayPeriod} />
              </TabPanel>
              <TabPanel id="config">
                <ConfigTab displayPeriod={displayPeriod} />
              </TabPanel>
            </Tabs>
          </Card>
        </VStack>
      </PageContainer>
    </DatesProvider>
  );
}

export const Route = createFileRoute("/_authenticated/user/goals")({
  component: GoalsDashboard,
  staticData: { application: "Merchandising" },
});
